import {
  Component, OnInit, ChangeDetectionStrategy, Output, EventEmitter, ChangeDetectorRef, ViewChild, Input, ElementRef
} from '@angular/core';
import { Observable } from 'rxjs';
import { SearchService } from '@ispm/shared/dynamic-form/services/search.service';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { Idrug } from '@ispm/therapies/interfaces/drug.interface';
import { Drug } from '@ispm/therapies/classes/Drug';
import { AutoComplete } from 'primeng/autocomplete';

@Component({
  selector: 'gen-therapies-add-new',
  templateUrl: './therapies-add-new.component.html',
  styleUrls: ['./therapies-add-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TherapiesAddNewComponent implements OnInit {
  @Input() allowTherapyFreeText: boolean = false;
  @Input() EnableAddingTrials: boolean = false;
  @Input() targetMaxLength: number = 30;
  @Output() addTherapyEmit: EventEmitter<Idrug> = new EventEmitter();
  @Output() addTrialEmit: EventEmitter<{ trialID: string, target: string }> = new EventEmitter();
  @Output() inprogressEmit: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('autoComplete') set newTherapyAutoCompleteChild(autocomplete: AutoComplete) {
    this.newTherapyAutoComplete = autocomplete;
  }
  @ViewChild('newTrail') set newTrailInputChild(input: ElementRef) {
    this.newTrailInput = input;
  }

  private newTherapyAutoComplete: AutoComplete;
  private newTrailInput: ElementRef;

  public addNewTherapyFlag: boolean = false;
  public addNewTrialFlag: boolean = false;

  set addNewTherapy(flag: boolean) {
    this.addNewTherapyFlag = flag;
    this.inprogressEmit.emit(this.addNewTherapyFlag);
    this.cf.markForCheck();
    this.cf.detectChanges();

    if (this.newTherapyAutoComplete) {
      this.newTherapyAutoComplete.focusInput();
    }
  }

  set addNewTrial(flag: boolean) {
    this.addNewTrialFlag = flag;
    this.inprogressEmit.emit(this.addNewTrialFlag);
    this.cf.markForCheck();
    this.cf.detectChanges();

    if (this.newTrailInput) {
      this.newTrailInput.nativeElement.focus();
    }
  }

  get addNewTherapy() {
    return this.addNewTherapyFlag;
  }

  get addNewTrial() {
    return this.addNewTrialFlag;
  }

  public newTherapy: Drug = new Drug({ source: 'manual', target: [], drugIds: []});
  public newTrial: { source: 'manual', trialID: string, target: string };
  public selectedTherapy: any;
  public options: any[];
  public options$: Observable<any[]> = Observable.of([]);
  private searchSource: string = '/valueset/drugbank?Synonyms=$(searchTerm)&_count=1000|transform=CommonName:display,id:code';
  private filterRegex = /[&#~_@%]/gi;

  constructor(
    private _search: SearchService,
    private cf: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.newTrial = { source: 'manual', trialID: '', target: '' };
  }

  discard() {
    this.newTherapy = new Drug({ source: 'manual', target: [], drugIds: []});
    this.newTrial = { source: 'manual', trialID: '', target: '' };
    this.selectedTherapy = null;
    this.addNewTherapy = false;
    this.addNewTrial = false;
  }

  add(addNewTherapy: boolean) {
    if (addNewTherapy) {
      if (this.newTherapy.name) {
        this.addTherapyEmit.emit(this.newTherapy);
        this.newTherapy = new Drug({ source: 'manual', target: [], drugIds: [], id: StringUtils.generateGuid()});
        this.selectedTherapy = null;
        this.addNewTherapy = false;
      } else {
        console.warn('Cannot add Therapy with no Name');
      }
    } else { // Adding a new trial
      if (this.newTrial.trialID) {
        this.addTrialEmit.emit(this.newTrial);
        this.newTrial = { source: 'manual', trialID: '', target: '' };
        this.addNewTrial = false;
      } else {
        console.warn('Cannot add Trial with no Trial ID');
      }
    }
  }

  updateTherapy(data) {
    if (((typeof data !== 'object' && typeof data !== 'function') || data === null) && this.allowTherapyFreeText) {
      this.newTherapy.name = data;
      this.newTherapy.drugIds = [data, Date.now().toString()];
      this.newTherapy.drugBankVersion = 'User Provided';
    } else if (data) {
      this.newTherapy.name = data.CommonName;
      this.newTherapy.drugIds = [data.id, data.DrugBankID];
      this.newTherapy.drugBankVersion = data.drugBankVersion;
    }
  }

  filterSynonyms(element, query) {
    query = query.replace(this.filterRegex, ' ').trim();
    element['Synonyms'] = element['Synonyms']
      .split('|')
      .filter(item => item.toLowerCase().contains(query.toLowerCase()))
      .map(item => item.trim())
      .join(', ');

    return element;
  }

  searchTherapy(event) {
    const query = event.query.replace(this.filterRegex, ' ').trim();

    if (query.length > 0) {
      this.options$ = this._search
        .query(this.searchSource, query)
        .map((elements: any[]) => {
          return elements.map(element => this._search.formalize(this.searchSource, element));
        })
        .map((elements: any[]) => {
          return elements.map(element => this.filterSynonyms(element, event.query));
        });
      this.options$.take(1).subscribe(data => {
        this.options = data;
        this.cf.detectChanges();
      });
    }
    else {
      this.options = [];
      this.cf.detectChanges();
    }
  }
}
